import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle,
  Clock,
  ListChecks,
  Minus,
  SkipForward,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/common/StatusBadge';
import { agentService } from '@/services/agent';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { AgentDecision } from '@/stores/agentStore';

const decisionIcons: Record<
  AgentDecision['decisionType'],
  React.ComponentType<{ className?: string }>
> = {
  buy: TrendingUp,
  sell: TrendingDown,
  hold: Minus,
  skip: SkipForward,
};

const outcomeConfig: Record<
  AgentDecision['outcome'],
  {
    color: 'success' | 'warning' | 'error' | 'info' | 'neutral';
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  pending: { color: 'info', icon: Clock },
  executed: { color: 'success', icon: CheckCircle },
  rejected: { color: 'warning', icon: XCircle },
  failed: { color: 'error', icon: XCircle },
  overridden: { color: 'neutral', icon: Minus },
  awaiting_approval: { color: 'info', icon: Clock },
  queued: { color: 'info', icon: ListChecks },
};

export function AgentDecisionLog() {
  const { data, isLoading } = useQuery({
    queryKey: ['agent', 'decisions'],
    queryFn: () => agentService.getDecisions(1, 10),
    refetchInterval: 10000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-12 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Recent Decisions</CardTitle>
      </CardHeader>
      <CardContent>
        {data?.decisions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No decisions yet</p>
        ) : (
          <div className="space-y-3">
            {data?.decisions.map((decision) => {
              const DecisionIcon = decisionIcons[decision.decisionType];
              const outcomeConf = outcomeConfig[decision.outcome];

              return (
                <div
                  key={decision.id}
                  className="flex items-start gap-3 text-sm border-b pb-2 last:border-0"
                >
                  <DecisionIcon
                    className={`h-4 w-4 mt-0.5 ${
                      decision.decisionType === 'buy'
                        ? 'text-green-500'
                        : decision.decisionType === 'sell'
                          ? 'text-red-500'
                          : 'text-muted-foreground'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{decision.symbol}</span>
                      <StatusBadge status={outcomeConf.color}>{decision.outcome}</StatusBadge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{decision.reasoning}</p>
                    {decision.quantity && decision.price && (
                      <p className="text-xs">
                        {decision.quantity} @ {formatCurrency(decision.price)}
                        {decision.notional != null && ` (${formatCurrency(decision.notional)})`}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {formatDate(decision.createdAt)}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {Math.round(decision.confidence * 100)}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AgentDecisionLog;
